import { useState, useEffect } from 'react';
import type { ConsentPreferences } from '../../utils/analytics/privacyConsent';
import {
  hasUserConsented,
  acceptAllCookies,
  acceptEssentialOnly,
  privacyConsent,
  PRIVACY_INFO,
} from '../../utils/analytics/privacyConsent';

interface CookieConsentProps {
  className?: string;
}

export function CookieConsent({ className = '' }: CookieConsentProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    analytics: false,
    performance: false,
    functional: true,
    marketing: false,
  });

  useEffect(() => {
    // Only show banner if user hasn't made a choice
    setShowBanner(!hasUserConsented());
    
    // Load current preferences
    setPreferences(privacyConsent.getPreferences());

    // Subscribe to consent changes
    const unsubscribe = privacyConsent.subscribe((newPreferences) => {
      setPreferences(newPreferences);
    });

    return unsubscribe;
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setShowBanner(false);
    setShowDetails(false);
  };

  const handleAcceptEssential = () => {
    acceptEssentialOnly();
    setShowBanner(false);
    setShowDetails(false);
  };

  const handleSavePreferences = () => {
    privacyConsent.updatePreferences(preferences);
    setShowBanner(false);
    setShowDetails(false);
  };

  const handlePreferenceChange = (type: keyof ConsentPreferences, value: boolean) => {
    setPreferences(prev => ({ ...prev, [type]: value }));
  };

  if (!showBanner) {
    return null;
  }

  if (showDetails) {
    return (
      <div className={`fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 ${className}`}>
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-charcoal dark:text-slate-100">Privacy Settings</h2>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
                aria-label="Close privacy settings"
              >
                ✕
              </button>
            </div>

            <p className="text-gray-600 dark:text-slate-400 mb-6">
              We use cookies to improve your experience and understand how our calculator is used. 
              You can customize your preferences below.
            </p>

            <div className="space-y-4 mb-6">
              {/* Functional Cookies - Always Required */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-charcoal dark:text-slate-100">Essential Cookies</h3>
                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                      Required for basic functionality like form submissions and navigation.
                    </p>
                  </div>
                  <div className="text-sm font-medium text-sa-green bg-green-100 dark:bg-green-900/50 px-3 py-1 rounded-full">
                    Always Active
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-charcoal dark:text-slate-100">Analytics Cookies</h3>
                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                      Help us understand how users interact with our calculator to improve the experience.
                    </p>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                      className="w-4 h-4 text-sa-green bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green focus:ring-2"
                    />
                    <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">Enable</span>
                  </label>
                </div>
              </div>

              {/* Performance Cookies */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-charcoal dark:text-slate-100">Performance Cookies</h3>
                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                      Monitor application performance and load times to ensure optimal experience.
                    </p>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={preferences.performance}
                      onChange={(e) => handlePreferenceChange('performance', e.target.checked)}
                      className="w-4 h-4 text-sa-green bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green focus:ring-2"
                    />
                    <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">Enable</span>
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-charcoal dark:text-slate-100">Marketing Cookies</h3>
                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                      Currently not used. Reserved for future marketing features.
                    </p>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                      className="w-4 h-4 text-sa-green bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green focus:ring-2"
                    />
                    <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">Enable</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Privacy Information */}
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-charcoal dark:text-slate-100 mb-2">What data do we collect?</h4>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1 mb-3">
                {PRIVACY_INFO.dataCollected.slice(0, 3).map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              
              <h4 className="font-semibold text-charcoal dark:text-slate-100 mb-2">What we don't collect:</h4>
              <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
                {PRIVACY_INFO.dataNotCollected.slice(0, 3).map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 btn-primary py-3"
              >
                Save My Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 btn-accent py-3"
              >
                Accept All
              </button>
              <button
                onClick={handleAcceptEssential}
                className="flex-1 btn-secondary py-3"
              >
                Essential Only
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Basic consent banner
  return (
    <div className={`fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 shadow-lg ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-charcoal dark:text-slate-100 mb-1">
                  We value your privacy
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  This website uses cookies to enhance your experience and help us improve our tax calculator. 
                  We don't collect personal financial information or income amounts.{' '}
                  <button
                    onClick={() => setShowDetails(true)}
                    className="text-sa-green hover:underline font-medium"
                  >
                    Learn more
                  </button>
                </p>
              </div>
              <button
                onClick={handleAcceptAll}
                className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 text-xl flex-shrink-0"
                aria-label="Accept all cookies and dismiss"
                title="Accept all cookies and dismiss"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 min-w-0">
            <button
              onClick={() => setShowDetails(true)}
              className="px-4 py-2 text-sa-green border border-sa-green rounded-lg hover:bg-sa-green/5 transition-colors text-sm font-medium"
            >
              Customize
            </button>
            <button
              onClick={handleAcceptEssential}
              className="btn-secondary text-sm"
            >
              Essential Only
            </button>
            <button
              onClick={handleAcceptAll}
              className="btn-accent text-sm"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Privacy settings toggle component (for footer)
export function PrivacySettings() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="text-sm text-gray-400 dark:text-slate-500 hover:text-gray-300 dark:hover:text-slate-300 underline"
      >
        Cookie Settings
      </button>
      
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CookieConsentSettings onClose={() => setShowModal(false)} />
          </div>
        </div>
      )}
    </>
  );
}

// Reusable settings component without the banner logic
function CookieConsentSettings({ onClose }: { onClose: () => void }) {
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    analytics: false,
    performance: false,
    functional: true,
    marketing: false,
  });

  useEffect(() => {
    // Load current preferences
    setPreferences(privacyConsent.getPreferences());
  }, []);

  const handleSavePreferences = () => {
    privacyConsent.updatePreferences(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAllCookies();
    onClose();
  };

  const handleAcceptEssential = () => {
    acceptEssentialOnly();
    onClose();
  };

  const handlePreferenceChange = (type: keyof ConsentPreferences, value: boolean) => {
    setPreferences(prev => ({ ...prev, [type]: value }));
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Privacy Settings</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
          aria-label="Close privacy settings"
        >
          ✕
        </button>
      </div>

      <p className="text-gray-600 dark:text-slate-400 mb-6">
        We use cookies to improve your experience and understand how our calculator is used. 
        You can customize your preferences below.
      </p>

      <div className="space-y-4 mb-6">
        {/* Essential Cookies - Always Required */}
        <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-slate-100">Essential Cookies</h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                Required for basic functionality like form submissions and navigation.
              </p>
            </div>
            <div className="text-sm font-medium text-sa-green bg-green-100 dark:bg-green-900/50 px-3 py-1 rounded-full">
              Always Active
            </div>
          </div>
        </div>

        {/* Analytics Cookies */}
        <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-slate-100">Analytics Cookies</h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                Help us understand how users interact with our calculator to improve the experience.
              </p>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                className="w-4 h-4 text-sa-green bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green focus:ring-2"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">Enable</span>
            </label>
          </div>
        </div>

        {/* Performance Cookies */}
        <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-slate-100">Performance Cookies</h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                Monitor application performance and load times to ensure optimal experience.
              </p>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.performance}
                onChange={(e) => handlePreferenceChange('performance', e.target.checked)}
                className="w-4 h-4 text-sa-green bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green focus:ring-2"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">Enable</span>
            </label>
          </div>
        </div>

        {/* Marketing Cookies */}
        <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-slate-100">Marketing Cookies</h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                Currently not used. Reserved for future marketing features.
              </p>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.marketing}
                onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                className="w-4 h-4 text-sa-green bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green focus:ring-2"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-slate-400">Enable</span>
            </label>
          </div>
        </div>
      </div>

      {/* Privacy Information */}
      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">What data do we collect?</h4>
        <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1 mb-3">
          {PRIVACY_INFO.dataCollected.slice(0, 3).map((item, index) => (
            <li key={index} className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              {item}
            </li>
          ))}
        </ul>
        
        <h4 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">What we don't collect:</h4>
        <ul className="text-sm text-gray-600 dark:text-slate-400 space-y-1">
          {PRIVACY_INFO.dataNotCollected.slice(0, 3).map((item, index) => (
            <li key={index} className="flex items-start">
              <span className="text-red-500 mr-2">✗</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleSavePreferences}
          className="flex-1 btn-primary py-3"
        >
          Save My Preferences
        </button>
        <button
          onClick={handleAcceptAll}
          className="flex-1 btn-accent py-3"
        >
          Accept All
        </button>
        <button
          onClick={handleAcceptEssential}
          className="flex-1 btn-secondary py-3"
        >
          Essential Only
        </button>
      </div>
    </div>
  );
}

export default CookieConsent;